import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHand } from '@fortawesome/pro-light-svg-icons/faHand';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons/faTriangleExclamation';
import { CatalogItemOwnership } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { LoadingView } from '../../common/views/LoadingView';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { ErrorAlert } from '../../common/views/ErrorAlert/ErrorAlert';
import { Alert } from '../../forms/components/Alert/Alert';
import { AlertLevel } from '../../forms/components/Alert/props';
import { Cover } from '../../music24/components/primitives/Cover';
import { useTrackDetails } from '../../music24/hooks/useTrackDetails';
import { CatalogClaimResponse, CatalogSuggestion } from '../../../redux/api/fymAPI.types';
import { ClaimWayChoice } from './claimWay/ClaimWayChoice';
import { ClaimConfirmFooter } from './claimWay/ClaimConfirmFooter';
import { useClaimWay } from './claimWay/useClaimWay';
import { ClaimRoute } from './claimWay/useClaimRoute';
import { CatalogClaimSnapshot } from './types';
import { CatalogServiceMarks } from './CatalogServiceMarks';
import { ConnectOption } from './ConnectOption';
import { buildClaimSnapshot } from './buildClaimSnapshot';
import { useClaimRelease } from './useClaimRelease';
import { ClaimMatchNote } from './release/ClaimMatchNote';
import { useGetClaimMatchQuery } from '../../../redux/api/fymAPI';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from './Catalog.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ClaimConfirmStepProps {
  accountId: number;
  catalogTrackMaid: string;
  /**
   * The details, when the release came off the artist's own profile. Its key is
   * a synthetic `profile:…` id that musicapi holds no track for, so a read by
   * maid would find nothing and the row carries everything the claim needs.
   */
  presetSnapshot?: CatalogClaimSnapshot;
  /** The account's own name, used when the catalog credits nobody. */
  fallbackArtistName: string;
  onBack: () => void;
  /**
   * Gets the claim answer: the saved row, so the sheet can ask for its master (design k1), and the
   * merge facts (design catalog-merge a4).
   */
  onClaimed: (claimed: CatalogClaimResponse) => void;
  /** The suggestion that the dialog opened on: it offers the merge (design catalog-merge c2, c3). */
  suggestion?: CatalogSuggestion;
  /** The address of the dialog: the way and the picked row. */
  claimRoute?: ClaimRoute;
  /** The release joined a song of the catalog (the merge way). */
  onMerged?: (claimed: CatalogClaimResponse) => void;
}

/**
 * Step two: what is being claimed, what it grants, and what it does not.
 *
 * The track is re-read by maid rather than trusted from the search hit: the
 * search index carries a title and a cover, and the claim stores the service
 * links too.
 */
const ClaimConfirmStepComponent: React.FC<ClaimConfirmStepProps> = ({
  accountId,
  catalogTrackMaid,
  presetSnapshot,
  fallbackArtistName,
  onBack,
  onClaimed,
  suggestion,
  claimRoute,
  onMerged,
}) => {
  const intl = useIntl();
  const claimWay = useClaimWay({ accountId, suggestion, route: claimRoute });
  const isMerge = claimWay.isOffered && claimWay.way === 'merge';
  const mergeTarget = isMerge ? claimWay.intoItem : undefined;
  const hasSuggestionMatch = suggestion?.matchesCatalogItem !== null && suggestion?.matchesCatalogItem !== undefined;
  const [ownership, setOwnership] = useState<CatalogItemOwnership>(CatalogItemOwnership.mine);
  // design g3: an unreleased row that looks like this track. A profile release has no musicapi
  // track to match, so it asks nothing. Linking is the default: it keeps the song files together.
  const { data: matchResponse } = useGetClaimMatchQuery(
    { accountId, maid: catalogTrackMaid },
    { skip: catalogTrackMaid.startsWith('profile:') || claimWay.isOffered }
  );
  // The way choice (design catalog-merge c2) replaces the note of design g3.
  const match = claimWay.isOffered ? null : (matchResponse?.item ?? null);
  const [isLinking, setIsLinking] = useState(true);
  const {
    data: track,
    isLoading,
    error,
    refetch,
  } = useTrackDetails({ trackId: catalogTrackMaid, enabled: presetSnapshot === undefined });
  const { claim, isLoading: isClaiming, error: claimError, isAlreadyClaimed } = useClaimRelease({ accountId });

  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const snapshot = useMemo(() => {
    if (presetSnapshot !== undefined) {
      return presetSnapshot;
    }
    return track ? buildClaimSnapshot(catalogTrackMaid, track, fallbackArtistName) : undefined;
  }, [catalogTrackMaid, fallbackArtistName, presetSnapshot, track]);

  const onConfirm = useCallback(() => {
    if (snapshot === undefined) {
      return;
    }
    if (isMerge) {
      if (mergeTarget === undefined) {
        return;
      }
      claim(snapshot, ownership, undefined, undefined, mergeTarget.uuid)
        .then((claimed) => {
          if (claimed !== null) {
            (onMerged ?? onClaimed)(claimed);
          }
        })
        .catch(console.error);
      return;
    }
    // A shown match that the artist declined: no automatic merge on the server. "Add as a new
    // release" next to a matched song is the same answer (design catalog-merge c3).
    const keepSeparate = (match !== null && !isLinking) || (claimWay.isOffered && hasSuggestionMatch);
    claim(snapshot, ownership, match !== null && isLinking ? match.uuid : undefined, keepSeparate)
      .then((claimed) => {
        if (claimed === null) {
          return;
        }
        onClaimed(claimed);
      })
      .catch(console.error);
  }, [
    claim,
    claimWay.isOffered,
    hasSuggestionMatch,
    isLinking,
    isMerge,
    match,
    mergeTarget,
    onClaimed,
    onMerged,
    ownership,
    snapshot,
  ]);

  if (error) {
    return (
      <ModalBody>
        <ErrorView error={error} retry={retry} />
      </ModalBody>
    );
  }

  // A preset snapshot leaves the track read switched off, so it reports neither
  // loading nor error and the sheet draws straight away.
  if (isLoading || snapshot === undefined) {
    return (
      <ModalBody>
        <LoadingView />
      </ModalBody>
    );
  }

  const subtitle = [snapshot.releaseType, snapshot.artistName].filter((part) => part !== undefined).join(' · ');

  return (
    <>
      <ModalBody className="d-flex flex-column gap-3">
        {isAlreadyClaimed ? (
          <Alert level={AlertLevel.info} center={false}>
            {intl.formatMessage({
              id: 'app.sml.catalog.claim.already',
              defaultMessage: 'This release is already in your catalog. Nothing to do.',
            })}
          </Alert>
        ) : (
          claimError !== undefined && <ErrorAlert error={claimError} />
        )}

        <div className={styles.selectedTrack}>
          <Cover src={snapshot.artworkUrl} label={snapshot.title} size={60} />
          <div>
            <div className={styles.selectedTrackTitle}>{snapshot.title}</div>
            <div className={styles.workSub}>{subtitle}</div>
            <div className="mt-2">
              <CatalogServiceMarks serviceLinks={snapshot.serviceLinks ?? null} />
            </div>
          </div>
        </div>

        {suggestion !== undefined && claimWay.isOffered && (
          <ClaimWayChoice suggestion={suggestion} claimWay={claimWay} />
        )}

        {match !== null && <ClaimMatchNote match={match} isLinking={isLinking} onChange={setIsLinking} />}

        {!isMerge && (
          <>
            <div className={styles.claimBox}>
              <FontAwesomeIcon icon={faHand} />
              <p>
                {intl.formatMessage({
                  id: 'app.sml.catalog.claim.grants',
                  defaultMessage:
                    'It joins your catalog straight away. Smartlinks, pre-saves and campaigns all work on it from the moment you press the button.',
                })}
              </p>
            </div>

            <div className={styles.warnBox}>
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <p>
                {intl.formatMessage({
                  id: 'app.sml.catalog.claim.limits',
                  defaultMessage:
                    'A claim is your word, not a service’s. We cannot show streams, listeners or playlist data for it, because reading those needs the artist account. Click and page numbers still work in full.',
                })}
              </p>
            </div>

            <div className={styles.options} role="radiogroup">
              <ConnectOption
                value={CatalogItemOwnership.mine}
                selected={ownership}
                onSelect={setOwnership}
                title={intl.formatMessage({
                  id: 'app.sml.catalog.claim.mine',
                  defaultMessage: 'Mine — my own release',
                })}
                description={intl.formatMessage({
                  id: 'app.sml.catalog.claim.mine_help',
                  defaultMessage: 'Shown as your release everywhere in Promote.',
                })}
              />
              <ConnectOption
                value={CatalogItemOwnership.featured}
                selected={ownership}
                onSelect={setOwnership}
                title={intl.formatMessage({
                  id: 'app.sml.catalog.claim.featured',
                  defaultMessage: 'I am a featured artist',
                })}
                description={intl.formatMessage({
                  id: 'app.sml.catalog.claim.featured_help',
                  defaultMessage: 'Promotable, but labelled so it never reads as your own single.',
                })}
              />
            </div>
          </>
        )}
      </ModalBody>
      <ClaimConfirmFooter
        isMerge={isMerge}
        mergeTarget={mergeTarget}
        isClaiming={isClaiming}
        onBack={onBack}
        onConfirm={onConfirm}
      />
    </>
  );
};

export const ClaimConfirmStep = TypedMemo(ClaimConfirmStepComponent);
